import React, { useState, useCallback, useRef, useEffect } from 'react';
// XLSX is dynamically imported inside parseFile() — keeps the ~600kB
// library out of the main bundle. The original `import * as XLSX from
// 'xlsx'` lived here.

import { API_BASE_URL } from '../config/api';
const API_BASE = API_BASE_URL;

/**
 * AddCandidateArchiveModal
 * 
 * Props:
 * - onClose: function to close modal
 * - onSuccess: function called after successful import/creation
 * - userType: 'client' | 'internal' — determines which API endpoint and database to use
 * 
 * Workflow: Manual Entry | Upload → Map Columns → Resolve Tags → Preview → Import
 */

// ===== FREE-TEXT PARSERS =====

/**
 * Parse a free-text position string like:
 * "VP at Goldman Sachs 2018-2022"
 * "Senior Developer, Google (2020-present)"
 * "Managing Director - JP Morgan, 2015 to 2021"
 */
// ===== POSITION PARSING =====

const MONTH_MAP = {
  'jan':1,'january':1,'feb':2,'february':2,'mar':3,'march':3,'apr':4,'april':4,
  'may':5,'jun':6,'june':6,'jul':7,'july':7,'aug':8,'august':8,
  'sep':9,'sept':9,'september':9,'oct':10,'october':10,'nov':11,'november':11,'dec':12,'december':12
};

// Known title keywords — if a segment contains these, it's likely a job title
const TITLE_KEYWORDS = /\b(engineer|developer|architect|designer|manager|director|analyst|consultant|specialist|coordinator|administrator|assistant|associate|lead|head|chief|officer|president|vp|vice[\s-]?president|evp|svp|avp|cto|cfo|ceo|coo|cio|cmo|partner|principal|counsel|advisor|strategist|recruiter|intern|fellow|scientist|researcher|professor|teacher|nurse|surgeon|physician|therapist|attorney|lawyer|accountant|auditor|planner|buyer|editor|writer|producer|founder|co-founder|operator|executive|representative|supervisor|foreman|technician|mechanic|electrician|paralegal|clerk)\b/i;

// Known company suffixes — if a segment ends with these, it's likely a company
const COMPANY_SUFFIXES = /\b(inc\.?|llc|ltd\.?|corp\.?|corporation|company|co\.?|group|partners|holdings|ventures|capital|solutions|consulting|technologies|tech|systems|services|associates|enterprises|global|international|bank|fund|advisors|management|plc)\s*$/i;

/**
 * Normalize a date string into YYYY-MM-DD format for database storage.
 * Handles: "2018", "Jan 2018", "January 2018", "01/2018", "2018-01", 
 * "1/2018", "2018/01", "March 15, 2020", "Q1 2020", "Spring 2020"
 */
function normalizeDate(raw) {
  if (!raw) return null;
  const s = raw.trim();
  
  // Already ISO: "2018-01-01"
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // YYYY-MM: "2018-01"
  if (/^\d{4}-\d{2}$/.test(s)) return s + '-01';
  // Just year: "2018"
  if (/^\d{4}$/.test(s)) return s + '-01-01';
  
  // Month Year: "Jan 2018", "January 2018"
  const monthYearRe = /^([A-Za-z]+)\s*,?\s*(\d{4})$/;
  const my = s.match(monthYearRe);
  if (my) {
    const m = MONTH_MAP[my[1].toLowerCase()];
    if (m) return `${my[2]}-${String(m).padStart(2,'0')}-01`;
  }
  
  // MM/YYYY or M/YYYY: "01/2018", "1/2018"
  const slashRe = /^(\d{1,2})\s*[/\-.]\s*(\d{4})$/;
  const sl = s.match(slashRe);
  if (sl && parseInt(sl[1]) >= 1 && parseInt(sl[1]) <= 12) {
    return `${sl[2]}-${String(parseInt(sl[1])).padStart(2,'0')}-01`;
  }
  
  // YYYY/MM: "2018/01"
  const yrSlash = /^(\d{4})\s*[/\-.]\s*(\d{1,2})$/;
  const ys = s.match(yrSlash);
  if (ys && parseInt(ys[2]) >= 1 && parseInt(ys[2]) <= 12) {
    return `${ys[1]}-${String(parseInt(ys[2])).padStart(2,'0')}-01`;
  }
  
  // "Month Day, Year": "March 15, 2020"
  const fullDateRe = /^([A-Za-z]+)\s+(\d{1,2}),?\s*(\d{4})$/;
  const fd = s.match(fullDateRe);
  if (fd) {
    const m = MONTH_MAP[fd[1].toLowerCase()];
    if (m) return `${fd[3]}-${String(m).padStart(2,'0')}-${String(parseInt(fd[2])).padStart(2,'0')}`;
  }
  
  // Quarter: "Q1 2020" → Jan, "Q2 2020" → Apr, etc.
  const qRe = /^Q(\d)\s*(\d{4})$/i;
  const q = s.match(qRe);
  if (q) {
    const months = { '1':'01','2':'04','3':'07','4':'10' };
    return `${q[2]}-${months[q[1]] || '01'}-01`;
  }
  
  return null; // Can't parse
}

/**
 * Determine if a text segment is more likely a job title or company name.
 * Returns 'title', 'company', or 'unknown'.
 */
function classifySegment(text) {
  if (!text) return 'unknown';
  const s = text.trim();
  if (TITLE_KEYWORDS.test(s)) return 'title';
  if (COMPANY_SUFFIXES.test(s)) return 'company';
  // Heuristic: starts with action words or level indicators
  if (/^(senior|junior|mid[\s-]?level|staff|principal|entry[\s-]?level|interim|acting|deputy|regional|global|group)/i.test(s)) return 'title';
  // Heuristic: very short strings with "of" pattern are often titles: "Head of Marketing"
  if (/\bof\b/i.test(s) && s.length < 40) return 'title';
  return 'unknown';
}

/**
 * Extract date range from text. Returns { start, end, isCurrent, cleaned }
 * where cleaned is the text with the date range removed.
 * 
 * Handles:
 * - "5/2024 to present", "2/2018 to 5/2024"
 * - "Jan 2018 - Mar 2022", "January 2018 to Present"
 * - "2018-2022", "2018 - present", "(2020-present)"
 * - "from 2018 to 2022", "since 2018"
 * - "(2018)", "2018" at end of string
 */
function extractDateRange(text) {
  let cleaned = text;
  let start = null, end = null, isCurrent = false;
  
  const currentRe = /present|current|now|ongoing/i;
  const SEP = /\s+(?:to|-|–|—)\s+|\s*[-–—]\s*/; // word "to" or dashes as separators
  
  // Pattern 1: MM/YYYY to MM/YYYY or present (MUST check before year-only patterns)
  // "5/2024 to present", "2/2018 to 5/2024", "11/2014 - 1/2018"
  const slashRangeRe = /,?\s*(\d{1,2}\/\d{4})\s+(?:to|-|–|—)\s+(\d{1,2}\/\d{4}|present|current|now|ongoing)\s*$/i;
  const slashMatch = text.match(slashRangeRe);
  if (slashMatch) {
    start = normalizeDate(slashMatch[1]);
    if (currentRe.test(slashMatch[2])) { isCurrent = true; }
    else { end = normalizeDate(slashMatch[2]); }
    cleaned = text.replace(slashRangeRe, '').trim().replace(/^[,;:\s]+|[,;:\s]+$/g, '');
    return { start, end, isCurrent, cleaned };
  }
  
  // Pattern 2: Month Year - Month Year / Present
  // "Jan 2018 - Mar 2022", "January 2018 to Present"
  const monthRangeRe = /,?\s*\(?\s*([A-Za-z]+\.?\s*\d{4})\s+(?:to|-|–|—)\s+([A-Za-z]+\.?\s*\d{4}|present|current|now|ongoing)\s*\)?\s*$/i;
  const monthRangeMatch = text.match(monthRangeRe);
  if (monthRangeMatch) {
    start = normalizeDate(monthRangeMatch[1].replace('.',''));
    if (currentRe.test(monthRangeMatch[2])) { isCurrent = true; } 
    else { end = normalizeDate(monthRangeMatch[2].replace('.','')); }
    cleaned = text.replace(monthRangeRe, '').trim().replace(/^[,;:\s]+|[,;:\s]+$/g, '');
    return { start, end, isCurrent, cleaned };
  }
  
  // Pattern 3: "from YYYY to YYYY" (MUST check before bare year range to consume "from" keyword)
  const fromToRe = /,?\s*\bfrom\s+(\d{1,2}\/\d{4}|[A-Za-z]+\.?\s*\d{4}|\d{4})\s+to\s+(\d{1,2}\/\d{4}|[A-Za-z]+\.?\s*\d{4}|\d{4}|present|current|now|ongoing)\s*$/i;
  const fromToMatch = text.match(fromToRe);
  if (fromToMatch) {
    start = normalizeDate(fromToMatch[1].trim());
    if (currentRe.test(fromToMatch[2])) { isCurrent = true; }
    else { end = normalizeDate(fromToMatch[2].trim()); }
    cleaned = text.replace(fromToRe, '').trim().replace(/^[,;:\s]+|[,;:\s]+$/g, '');
    return { start, end, isCurrent, cleaned };
  }
  
  // Pattern 4: Year - Year / Present (with optional parentheses)
  // "2018-2022", "2018 - present", "(2020-present)", "2015 to 2021"
  const yearRangeRe = /,?\s*\(?\s*(\d{4})\s+(?:to|-|–|—)\s+(\d{4}|present|current|now|ongoing)\s*\)?\s*$/i;
  const yearRangeMatch = text.match(yearRangeRe);
  if (yearRangeMatch) {
    start = normalizeDate(yearRangeMatch[1]);
    if (currentRe.test(yearRangeMatch[2])) { isCurrent = true; }
    else { end = normalizeDate(yearRangeMatch[2]); }
    cleaned = text.replace(yearRangeRe, '').trim().replace(/^[,;:\s]+|[,;:\s]+$/g, '');
    return { start, end, isCurrent, cleaned };
  }
  
  // Pattern 4b: Compact year range with dash only (no spaces): "2018-2022", "(2020-2023)"
  const compactYearRe = /,?\s*\(?\s*(\d{4})[-–—](\d{4}|present|current|now|ongoing)\s*\)?\s*$/i;
  const compactMatch = text.match(compactYearRe);
  if (compactMatch) {
    start = normalizeDate(compactMatch[1]);
    if (currentRe.test(compactMatch[2])) { isCurrent = true; }
    else { end = normalizeDate(compactMatch[2]); }
    cleaned = text.replace(compactYearRe, '').trim().replace(/^[,;:\s]+|[,;:\s]+$/g, '');
    return { start, end, isCurrent, cleaned };
  }
  
  // Pattern 5: "since 2018", "since Jan 2020"
  const sinceRe = /,?\s*\bsince\s+([A-Za-z]*\s*\d{4})/i;
  const sinceMatch = text.match(sinceRe);
  if (sinceMatch) {
    start = normalizeDate(sinceMatch[1].trim());
    isCurrent = true;
    cleaned = text.replace(sinceRe, '').trim().replace(/^[,;:\s]+|[,;:\s]+$/g, '');
    return { start, end: null, isCurrent, cleaned };
  }
  
  // Pattern 6: Single year at end: "(2018)" or ", 2018"
  const singleYearRe = /[,(]?\s*(\d{4})\s*\)?\s*$/;
  const singleMatch = text.match(singleYearRe);
  if (singleMatch) {
    start = normalizeDate(singleMatch[1]);
    cleaned = text.replace(singleYearRe, '').trim().replace(/^[,;:\s]+|[,;:\s]+$/g, '');
    return { start, end: null, isCurrent: false, cleaned };
  }
  
  return { start: null, end: null, isCurrent: false, cleaned: text.trim() };
}

/**
 * Parse a single position text into { company_name, job_title, start_date, end_date, is_current }
 * 
 * Handles many formats:
 * - "Software Engineer at Google"
 * - "VP of Sales, Acme Corp, 2019-2023"
 * - "Director of Marketing - Meta - 2020 to Present"
 * - "Worked as a software engineer at Microsoft from 2015 to 2020"
 * - "Google | Senior Engineer | 2020-2023"
 * - "Currently serving as CTO at Startup.io"
 * - "Former Head of Product at Stripe"
 * - "2019-2022: Project Manager at Amazon"
 * - "Senior Analyst; Goldman Sachs; 2017-2021"
 */
function parsePositionText(text) {
  if (!text || !text.trim()) return null;
  let s = text.trim();
  
  // Remove leading/trailing quotes
  s = s.replace(/^["']|["']$/g, '').trim();
  if (!s) return null;
  
  let title = null, company = null;
  
  // Strip common prefixes
  s = s.replace(/^(currently|presently)\s+(serving\s+as|working\s+as|employed\s+as|acting\s+as)?\s*/i, '');
  s = s.replace(/^(formerly|previously|former|ex[\s-]?)\s*/i, '');
  s = s.replace(/^(worked|working|employed|serves?|served|serving|acting)\s+(as\s+(an?\s+)?)?/i, '');
  s = s.replace(/^(i\s+am\s+(an?\s+)?|i\s+was\s+(an?\s+)?|i\s+work(ed)?\s+as\s+(an?\s+)?)/i, '');
  s = s.trim();
  
  // Extract dates first (removes them from the string)
  const { start, end, isCurrent, cleaned } = extractDateRange(s);
  s = cleaned;
  
  // Remove trailing punctuation
  s = s.replace(/^[,;:\s|]+|[,;:\s|]+$/g, '').trim();
  if (!s) return null;
  
  // Strategy 1: Pipe-separated — "Title | Company" or "Company | Title"
  if (s.includes('|')) {
    const parts = s.split('|').map(p => p.trim()).filter(Boolean);
    if (parts.length >= 2) {
      const c0 = classifySegment(parts[0]);
      const c1 = classifySegment(parts[1]);
      if (c0 === 'company' && c1 !== 'company') { company = parts[0]; title = parts[1]; }
      else if (c1 === 'company' && c0 !== 'company') { title = parts[0]; company = parts[1]; }
      else if (c0 === 'title') { title = parts[0]; company = parts[1]; }
      else { title = parts[0]; company = parts[1]; }
      return buildPosition(title, company, start, end, isCurrent);
    }
  }
  
  // Strategy 2: "at" or "@" separator — most natural: "Title at Company"
  const atRe = /^(.+?)\s+(?:at|@)\s+(.+)$/i;
  const atMatch = s.match(atRe);
  if (atMatch) {
    title = atMatch[1].trim();
    company = atMatch[2].trim();
    return buildPosition(title, company, start, end, isCurrent);
  }
  
  // Strategy 3: "for" separator — "Worked for Google as Engineer" → already stripped "worked"
  // Check "for Company as Title"
  const forAsRe = /^(?:for\s+)?(.+?)\s+as\s+(.+)$/i;
  const forAsMatch = s.match(forAsRe);
  if (forAsMatch) {
    company = forAsMatch[1].trim().replace(/^for\s+/i, '');
    title = forAsMatch[2].trim();
    return buildPosition(title, company, start, end, isCurrent);
  }
  
  // Strategy 4: Semicolon-separated — "Title; Company"
  if (s.includes(';')) {
    const parts = s.split(';').map(p => p.trim()).filter(Boolean);
    if (parts.length >= 2) {
      return classifyAndBuild(parts, start, end, isCurrent);
    }
  }
  
  // Strategy 5: Dash-separated — but only if dash isn't part of a compound title
  // "Director of Marketing - Meta" but NOT "Co-founder"
  const dashParts = s.split(/\s+[-–—]\s+/);
  if (dashParts.length >= 2) {
    return classifyAndBuild(dashParts, start, end, isCurrent);
  }
  
  // Strategy 6: Comma-separated — "VP of Sales, Acme Corp"
  // Tricky because titles can have commas: "VP of Sales, Marketing, and Ops"
  const commaParts = s.split(',').map(p => p.trim()).filter(Boolean);
  if (commaParts.length >= 2) {
    // Try to find the split point where company starts
    // Check last segment first (most common: "Title, Company")
    const lastPart = commaParts[commaParts.length - 1];
    if (classifySegment(lastPart) === 'company' || commaParts.length === 2) {
      if (commaParts.length === 2) {
        return classifyAndBuild(commaParts, start, end, isCurrent);
      }
      // Multiple commas: company is likely the last part
      company = lastPart;
      title = commaParts.slice(0, -1).join(', ');
      return buildPosition(title, company, start, end, isCurrent);
    }
  }
  
  // Strategy 7: Just a single segment — try to determine if title or company
  const cls = classifySegment(s);
  if (cls === 'company') {
    company = s;
  } else {
    title = s;
  }
  
  return buildPosition(title, company, start, end, isCurrent);
}

/** Classify two parts and build position */
function classifyAndBuild(parts, start, end, isCurrent) {
  const p0 = parts[0].trim();
  const p1 = parts.length > 2 ? parts.slice(1).join(', ').trim() : parts[1].trim();
  
  const c0 = classifySegment(p0);
  const c1 = classifySegment(p1);
  
  let title, company;
  if (c0 === 'company' && c1 !== 'company') { company = p0; title = p1; }
  else if (c1 === 'company' && c0 !== 'company') { title = p0; company = p1; }
  else if (c0 === 'title') { title = p0; company = p1; }
  else if (c1 === 'title') { company = p0; title = p1; }
  else { title = p0; company = p1; } // Default: first is title
  
  return buildPosition(title, company, start, end, isCurrent);
}

/** Clean and build the final position object */
function buildPosition(title, company, start, end, isCurrent) {
  // Clean up
  if (title) title = title.replace(/^\(|\)$/g, '').replace(/^["']|["']$/g, '').trim();
  if (company) company = company.replace(/^\(|\)$/g, '').replace(/^["']|["']$/g, '').trim();
  
  if (!title && !company) return null;
  
  // If start exists but no end and text didn't explicitly say "present", check if recent
  const autoIsCurrent = isCurrent || (!end && !!start);
  
  return {
    company_name: company || null,
    job_title: title || null,
    start_date: start || null,
    end_date: end || null,
    is_current: autoIsCurrent
  };
}

/**
 * Parse a cell that may contain MULTIPLE positions separated by newlines or semicolons.
 * Returns an array of position objects.
 * 
 * Handles:
 * - "Engineer at Google (2018-2020); Analyst at Meta (2020-2022)"
 * - "Google - Engineer\nMeta - Analyst" (newline separated)
 * - Single position text
 */
function parseMultiplePositions(text) {
  if (!text || !text.trim()) return [];
  
  // Split by newline first (most reliable multi-position delimiter)
  let segments = text.split(/[\n\r]+/).map(s => s.trim()).filter(Boolean);
  
  // If only one segment, try splitting by semicolons (but be careful not to split within a position)
  if (segments.length === 1) {
    // Only split by semicolons if they appear to separate distinct positions
    // Heuristic: if there's a semicolon AND both sides have content > 5 chars
    const semiParts = text.split(';').map(s => s.trim()).filter(s => s.length > 3);
    if (semiParts.length > 1) {
      segments = semiParts;
    }
  }
  
  const positions = [];
  for (const seg of segments) {
    const parsed = parsePositionText(seg);
    if (parsed) positions.push(parsed);
  }
  return positions;
}

// ===== EDUCATION PARSING =====

// Known degree patterns — order matters (longer first)
const DEGREE_RE = /\b(Doctor of Philosophy|Doctor of Business Administration|Doctor of Education|Master of Business Administration|Master of Fine Arts|Master of Science|Master of Arts|Bachelor of Science|Bachelor of Arts|Bachelor of Engineering|Associate of Science|Associate of Arts|Juris Doctor|Doctor of Medicine|Doctor of Pharmacy|Master of Laws|Master of Public Administration|Master of Public Health|Ph\.?D\.?|D\.?B\.?A\.?|Ed\.?D\.?|Pharm\.?D\.?|MBA|MFA|MPA|MPH|M\.?S\.?|M\.?A\.?|B\.?S\.?|B\.?A\.?|B\.?E\.?|B\.?Sc\.?|M\.?Sc\.?|B\.?Eng\.?|M\.?Eng\.?|J\.?D\.?|M\.?D\.?|LL\.?M\.?|LL\.?B\.?|A\.?S\.?|A\.?A\.?|Associate'?s?|Bachelor'?s?|Master'?s?|Doctorate|Diploma|Certificate|GED|High School Diploma)\b/i;

// Known institution keywords
const INSTITUTION_KEYWORDS = /\b(university|college|institute|school|academy|polytechnic|conservatory|seminary)\b/i;

/**
 * Parse a single education entry. Handles many formats:
 * - "Bachelor of Science (BSc), Economics, Swansea University, 1996 to 1999"
 * - "MBA from Wharton, 2015"
 * - "BS Computer Science, MIT (2010-2014)"
 * - "Harvard University - Economics"
 * - "Pangbourne College" (just institution)
 * - "PhD in Physics, Stanford University, 2018-2022"
 * - "MS, Computer Science, Georgia Tech"
 */
function parseEducationText(text) {
  if (!text || !text.trim()) return null;
  let s = text.trim();
  
  // Remove leading/trailing quotes
  s = s.replace(/^["']|["']$/g, '').trim();
  if (!s) return null;
  
  let institution = null, degree = null, field = null, start = null, end = null;
  
  // Extract dates first
  const dateInfo = extractDateRange(s);
  start = dateInfo.start;
  end = dateInfo.end;
  let cleaned = dateInfo.cleaned;
  
  // Remove parenthetical abbreviations and merge them: "Bachelor of Science (BSc)" → "Bachelor of Science"
  // But capture it for degree detection
  const parenAbbrev = cleaned.match(/\(([A-Z][A-Za-z.]{1,8})\)/);
  if (parenAbbrev) {
    cleaned = cleaned.replace(/\s*\([A-Z][A-Za-z.]{1,8}\)/, '').trim();
  }
  
  // Clean up
  cleaned = cleaned.replace(/^[,;:\s]+|[,;:\s]+$/g, '').trim();
  if (!cleaned) {
    if (parenAbbrev) return { institution: null, degree_type: parenAbbrev[1], field_of_study: null, start_date: start, end_date: end };
    return null;
  }
  
  // Strategy 1: "degree from institution"
  const fromParts = cleaned.split(/\s+from\s+/i);
  if (fromParts.length >= 2) {
    const degreePart = fromParts[0].trim();
    institution = fromParts.slice(1).join(' from ').trim();
    const dMatch = degreePart.match(DEGREE_RE);
    if (dMatch) {
      degree = dMatch[0];
      field = degreePart.replace(DEGREE_RE, '').replace(/^[\s,]+|[\s,]+$/g, '').replace(/^in\s+/i, '').trim() || null;
    } else {
      degree = degreePart;
    }
    return buildEducation(institution, degree, field, start, end, parenAbbrev);
  }
  
  // Strategy 2: Comma-separated segments — most common format
  // "Bachelor of Science, Economics, Swansea University"
  // "MBA, Finance, Wharton School"
  // "Swansea University, Economics"
  const commaParts = cleaned.split(',').map(p => p.trim()).filter(Boolean);
  
  if (commaParts.length >= 2) {
    // Classify each segment
    const classified = commaParts.map(part => ({
      text: part,
      isDegree: DEGREE_RE.test(part),
      isInstitution: INSTITUTION_KEYWORDS.test(part),
      degreeMatch: part.match(DEGREE_RE)
    }));
    
    // Find the degree segment
    const degreeIdx = classified.findIndex(c => c.isDegree);
    // Find the institution segment (skip the degree segment to avoid overlap like "High School Diploma")
    const instIdx = classified.findIndex((c, i) => c.isInstitution && i !== degreeIdx);
    
    if (degreeIdx >= 0 && instIdx >= 0) {
      // Both found — everything else is field of study
      degree = classified[degreeIdx].degreeMatch[0];
      const degreeRemainder = classified[degreeIdx].text.replace(DEGREE_RE, '').replace(/^[\s,]+|[\s,]+$/g, '').replace(/^in\s+/i, '').trim();
      institution = classified[instIdx].text;
      
      // Field: whatever segment(s) aren't the degree or institution
      const fieldParts = classified.filter((_, i) => i !== degreeIdx && i !== instIdx).map(c => c.text);
      if (degreeRemainder) fieldParts.unshift(degreeRemainder);
      field = fieldParts.join(', ').trim() || null;
    } else if (degreeIdx >= 0) {
      // Degree found, no institution keyword — last non-degree segment is probably institution
      degree = classified[degreeIdx].degreeMatch[0];
      const degreeRemainder = classified[degreeIdx].text.replace(DEGREE_RE, '').replace(/^[\s,]+|[\s,]+$/g, '').replace(/^in\s+/i, '').trim();
      const others = classified.filter((_, i) => i !== degreeIdx);
      if (others.length > 0) {
        institution = others[others.length - 1].text;
        const fieldParts = others.slice(0, -1).map(c => c.text);
        if (degreeRemainder) fieldParts.unshift(degreeRemainder);
        field = fieldParts.join(', ').trim() || null;
      } else if (degreeRemainder) {
        field = degreeRemainder;
      }
    } else if (instIdx >= 0) {
      // Institution found, no degree keyword — first segment might be degree/field
      institution = classified[instIdx].text;
      const others = classified.filter((_, i) => i !== instIdx).map(c => c.text);
      if (others.length > 0) {
        degree = others[0];
        field = others.slice(1).join(', ').trim() || null;
      }
    } else {
      // Neither keyword found — use heuristics
      // First part might be degree, last part might be institution
      if (commaParts.length === 2) {
        degree = commaParts[0];
        institution = commaParts[1];
      } else {
        degree = commaParts[0];
        institution = commaParts[commaParts.length - 1];
        field = commaParts.slice(1, -1).join(', ').trim() || null;
      }
    }
    
    return buildEducation(institution, degree, field, start, end, parenAbbrev);
  }
  
  // Strategy 3: Dash-separated — "Harvard University - Economics"
  const dashParts = cleaned.split(/\s+[-–—]\s+/);
  if (dashParts.length >= 2) {
    const part0 = dashParts[0].trim();
    const part1 = dashParts.slice(1).join(' - ').trim();
    
    if (DEGREE_RE.test(part0)) {
      degree = part0.match(DEGREE_RE)[0];
      field = part0.replace(DEGREE_RE, '').replace(/^[\s,]+|[\s,]+$/g, '').replace(/^in\s+/i, '').trim() || null;
      institution = part1;
    } else if (INSTITUTION_KEYWORDS.test(part0)) {
      institution = part0;
      if (DEGREE_RE.test(part1)) {
        degree = part1.match(DEGREE_RE)[0];
        field = part1.replace(DEGREE_RE, '').replace(/^[\s,]+|[\s,]+$/g, '').replace(/^in\s+/i, '').trim() || null;
      } else {
        degree = part1;
      }
    } else {
      institution = part0;
      degree = part1;
    }
    
    return buildEducation(institution, degree, field, start, end, parenAbbrev);
  }
  
  // Strategy 4: Single segment — degree or institution
  if (DEGREE_RE.test(cleaned)) {
    degree = cleaned.match(DEGREE_RE)[0];
    field = cleaned.replace(DEGREE_RE, '').replace(/^[\s,]+|[\s,]+$/g, '').replace(/^in\s+/i, '').trim() || null;
  } else {
    // Likely just an institution name
    institution = cleaned;
  }
  
  return buildEducation(institution, degree, field, start, end, parenAbbrev);
}

function buildEducation(institution, degree, field, start, end, parenAbbrev) {
  // Clean up
  if (institution) institution = institution.replace(/^\(|\)$/g, '').replace(/^["']|["']$/g, '').trim();
  if (degree) degree = degree.replace(/^\(|\)$/g, '').replace(/^["']|["']$/g, '').trim();
  if (field) field = field.replace(/^\(|\)$/g, '').replace(/^["']|["']$/g, '').replace(/^in\s+/i, '').trim();
  
  // Use parenthetical abbreviation as degree if no degree found
  if (!degree && parenAbbrev) degree = parenAbbrev[1];
  
  if (!institution && !degree) return null;
  return { institution: institution || null, degree_type: degree || null, field_of_study: field || null, start_date: start, end_date: end };
}

/**
 * Parse a cell with multiple education entries separated by semicolons or newlines.
 * "Bachelor of Science (BSc), Economics, Swansea University, 1996 to 1999; Pangbourne College"
 */
function parseMultipleEducation(text) {
  if (!text || !text.trim()) return [];
  
  let segments = text.split(/[\n\r]+/).map(s => s.trim()).filter(Boolean);
  
  if (segments.length === 1) {
    const semiParts = text.split(';').map(s => s.trim()).filter(s => s.length > 2);
    if (semiParts.length > 1) segments = semiParts;
  }
  
  const results = [];
  for (const seg of segments) {
    const parsed = parseEducationText(seg);
    if (!parsed) continue;
    
    // If this entry has ONLY an institution (no degree), and the previous entry
    // has both degree + institution, this is likely a sub-institution (e.g. college within university).
    // Merge it: "Swansea University" + "Pangbourne College" → "Swansea University, Pangbourne College"
    if (parsed.institution && !parsed.degree_type && !parsed.field_of_study && results.length > 0) {
      const prev = results[results.length - 1];
      if (prev.institution && prev.degree_type) {
        prev.institution = `${prev.institution}, ${parsed.institution}`;
        continue; // Don't add as separate entry
      }
    }
    
    results.push(parsed);
  }
  return results;
}

// ===== CERTIFICATION PARSING =====

/**
 * Parse a single certification entry.
 * - "AWS Solutions Architect, Amazon, 2022"
 * - "PMP from PMI, expires 2025"
 * - "CFA Level 3, CFA Institute (2019)"
 * - "Certified Scrum Master"
 */
function parseCertificationText(text) {
  if (!text || !text.trim()) return null;
  let s = text.trim().replace(/^["']|["']$/g, '').trim();
  if (!s) return null;
  
  let certName = null, org = null, issueDate = null, expDate = null;
  
  // Extract expiration date: "expires 2025", "exp. 12/2025"
  const expRe = /,?\s*(?:expires?|exp\.?|expiration)\s*:?\s*(\d{1,2}\/\d{4}|\d{4}|[A-Za-z]+\s*\d{4})\s*$/i;
  const expMatch = s.match(expRe);
  if (expMatch) {
    expDate = normalizeDate(expMatch[1]);
    s = s.replace(expRe, '').trim();
  }
  
  // Extract date range or single date
  const dateInfo = extractDateRange(s);
  issueDate = dateInfo.start;
  if (!expDate && dateInfo.end) expDate = dateInfo.end;
  s = dateInfo.cleaned;
  
  // Clean
  s = s.replace(/^[,;:\s]+|[,;:\s]+$/g, '').trim();
  if (!s) return null;
  
  // Strategy 1: "Cert from/by Org"
  const fromSplit = s.split(/\s+(?:from|by|through|via)\s+/i);
  if (fromSplit.length >= 2) {
    certName = fromSplit[0].trim();
    org = fromSplit.slice(1).join(' ').trim();
    return buildCert(certName, org, issueDate, expDate);
  }
  
  // Strategy 2: Comma-separated — "CFA Level 3, CFA Institute"
  const commaParts = s.split(',').map(p => p.trim()).filter(Boolean);
  if (commaParts.length >= 2) {
    certName = commaParts[0];
    org = commaParts.slice(1).join(', ').trim();
    return buildCert(certName, org, issueDate, expDate);
  }
  
  // Strategy 3: Dash-separated
  const dashParts = s.split(/\s+[-–—]\s+/);
  if (dashParts.length >= 2) {
    certName = dashParts[0].trim();
    org = dashParts.slice(1).join(' - ').trim();
    return buildCert(certName, org, issueDate, expDate);
  }
  
  // Single segment — just the cert name
  certName = s;
  return buildCert(certName, org, issueDate, expDate);
}

function buildCert(name, org, issueDate, expDate) {
  if (name) name = name.replace(/^\(|\)$/g, '').replace(/^["']|["']$/g, '').trim();
  if (org) org = org.replace(/^\(|\)$/g, '').replace(/^["']|["']$/g, '').trim();
  if (!name) return null;
  return { certification_name: name, issuing_organization: org || null, issue_date: issueDate || null, expiration_date: expDate || null };
}

/**
 * Parse a cell with multiple certifications separated by semicolons or newlines.
 */
function parseMultipleCertifications(text) {
  if (!text || !text.trim()) return [];
  
  let segments = text.split(/[\n\r]+/).map(s => s.trim()).filter(Boolean);
  
  if (segments.length === 1) {
    const semiParts = text.split(';').map(s => s.trim()).filter(s => s.length > 2);
    if (semiParts.length > 1) segments = semiParts;
  }
  
  const results = [];
  for (const seg of segments) {
    const parsed = parseCertificationText(seg);
    if (parsed) results.push(parsed);
  }
  return results;
}


// ===== COMPONENT =====
const AddCandidateArchiveModal = ({ onClose, onSuccess, userType: userTypeProp = 'client' }) => {
  // Auto-detect user type from stored user data — prop is fallback
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const userType = storedUser.user_type === 'client' ? 'client' 
    : (userTypeProp !== 'client' ? userTypeProp : (storedUser.user_type || userTypeProp));
  
  console.log('[AddCandidateArchiveModal] userTypeProp:', userTypeProp, 'storedUser.user_type:', storedUser.user_type, 'resolved userType:', userType);
  
  const [activeTab, setActiveTab] = useState('manual');

  // Manual entry state
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', linkedin: '' });
  const [saving, setSaving] = useState(false);

  // Upload state
  const [uploadedFile, setUploadedFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [fileColumns, setFileColumns] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [uploadStep, setUploadStep] = useState('upload'); // 'upload','map','resolve-tags','resolve-firms','preview','importing'
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [importResults, setImportResults] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Dynamic group counts
  const [positionGroupCount, setPositionGroupCount] = useState(1);
  const [educationGroupCount, setEducationGroupCount] = useState(1);
  const [certGroupCount, setCertGroupCount] = useState(1);
  const [tagSlotCount, setTagSlotCount] = useState(3);

  // Tag resolution state
  const [systemTags, setSystemTags] = useState([]);       // Tags from the system
  const [uniqueCsvTags, setUniqueCsvTags] = useState([]);  // Unique tag strings found in CSV
  const [tagMappings, setTagMappings] = useState({});      // { csvTagText: { action: 'map'|'create'|'skip', tagId: ..., tagName: ..., color: 'blue' } }
  const [loadingTags, setLoadingTags] = useState(false);

  // Firm resolution state
  const [uniqueCompanies, setUniqueCompanies] = useState([]);
  const [firmMappings, setFirmMappings] = useState({}); // { companyName: { action: 'map'|'create', firmId, standardName } }
  const [allFirmsForUpload, setAllFirmsForUpload] = useState([]);
  const [firmSearches, setFirmSearches] = useState({}); // { companyName: searchText }

  // Custom field definitions + duplicate handling
  const [customFieldDefs, setCustomFieldDefs] = useState([]); // [{ id, field_name, field_key, field_type, is_unique_key }]
  const [dedupField, setDedupField] = useState('none');        // 'none', 'name', or a custom field key
  const [showNewFieldForm, setShowNewFieldForm] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldIsKey, setNewFieldIsKey] = useState(false);

  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);

  // Prevent browser default drag behavior at document level
  useEffect(() => {
    const preventDefaults = (e) => { e.preventDefault(); e.stopPropagation(); };
    document.addEventListener('dragover', preventDefaults);
    document.addEventListener('drop', preventDefaults);
    return () => {
      document.removeEventListener('dragover', preventDefaults);
      document.removeEventListener('drop', preventDefaults);
    };
  }, []);

  // ===== BUILD AVAILABLE FIELDS =====
  const buildAvailableFields = () => {
    const fields = [];

    // Basic Info
    fields.push({ key: 'name', label: 'Full Name', group: 'Basic Info', required: true });
    fields.push({ key: 'first_name', label: 'First Name', group: 'Basic Info' });
    fields.push({ key: 'last_name', label: 'Last Name', group: 'Basic Info' });
    fields.push({ key: 'email', label: 'Email', group: 'Basic Info' });
    fields.push({ key: 'phone', label: 'Phone', group: 'Basic Info' });
    fields.push({ key: 'linkedin', label: 'LinkedIn URL', group: 'Basic Info' });
    fields.push({ key: 'current_title', label: 'Current Title', group: 'Basic Info' });
    fields.push({ key: 'current_company', label: 'Current Company', group: 'Basic Info' });
    fields.push({ key: 'location', label: 'Location (full)', group: 'Basic Info' });
    fields.push({ key: 'loc_city', label: 'Location — City', group: 'Location' });
    fields.push({ key: 'loc_state', label: 'Location — State/Province', group: 'Location' });
    fields.push({ key: 'loc_country', label: 'Location — Country', group: 'Location' });
    fields.push({ key: 'loc_zip', label: 'Location — Zip/Postal Code', group: 'Location' });
    fields.push({ key: 'referral_source', label: 'Referral Source', group: 'Basic Info' });
    fields.push({ key: 'notes', label: 'Notes', group: 'Basic Info' });

    // Tags — only combined field, individual per-candidate tags resolved in step 2
    fields.push({ key: 'tags_combined', label: 'Tags (comma-separated)', group: 'Tags' });

    // Position History
    fields.push({ key: 'pos_combined', label: 'All Positions (one column)', group: 'Position History' });
    for (let i = 1; i <= positionGroupCount; i++) {
      const prefix = positionGroupCount === 1 ? '' : ` ${i}`;
      fields.push({ key: `pos_${i}_text`, label: `Position${prefix} (sentence — auto-parsed)`, group: `Position${prefix} History` });
      fields.push({ key: `pos_${i}_company`, label: `Position${prefix} Company`, group: `Position${prefix} History` });
      fields.push({ key: `pos_${i}_title`, label: `Position${prefix} Title`, group: `Position${prefix} History` });
      fields.push({ key: `pos_${i}_start`, label: `Position${prefix} Start Date`, group: `Position${prefix} History` });
      fields.push({ key: `pos_${i}_end`, label: `Position${prefix} End Date`, group: `Position${prefix} History` });
    }

    // Education
    fields.push({ key: 'edu_combined', label: 'All Education (one column)', group: 'Education' });
    for (let i = 1; i <= educationGroupCount; i++) {
      const prefix = educationGroupCount === 1 ? '' : ` ${i}`;
      fields.push({ key: `edu_${i}_text`, label: `Education${prefix} (sentence — auto-parsed)`, group: `Education${prefix}` });
      fields.push({ key: `edu_${i}_institution`, label: `Education${prefix} Institution`, group: `Education${prefix}` });
      fields.push({ key: `edu_${i}_degree`, label: `Education${prefix} Degree Type`, group: `Education${prefix}` });
      fields.push({ key: `edu_${i}_field`, label: `Education${prefix} Field of Study`, group: `Education${prefix}` });
      fields.push({ key: `edu_${i}_start`, label: `Education${prefix} Start Date`, group: `Education${prefix}` });
      fields.push({ key: `edu_${i}_end`, label: `Education${prefix} End Date`, group: `Education${prefix}` });
    }

    // Certifications
    fields.push({ key: 'cert_combined', label: 'All Certifications (one column)', group: 'Certifications' });
    for (let i = 1; i <= certGroupCount; i++) {
      const prefix = certGroupCount === 1 ? '' : ` ${i}`;
      fields.push({ key: `cert_${i}_name`, label: `Certification${prefix} Name`, group: `Certification${prefix}` });
      fields.push({ key: `cert_${i}_org`, label: `Certification${prefix} Organization`, group: `Certification${prefix}` });
      fields.push({ key: `cert_${i}_date`, label: `Certification${prefix} Issue Date`, group: `Certification${prefix}` });
      fields.push({ key: `cert_${i}_exp`, label: `Certification${prefix} Expiration`, group: `Certification${prefix}` });
    }

    // Compensation
    fields.push({ key: 'current_base_salary', label: 'Current Base Salary', group: 'Compensation' });
    fields.push({ key: 'current_total_comp', label: 'Current Total Comp', group: 'Compensation' });
    fields.push({ key: 'expected_base', label: 'Expected Base', group: 'Compensation' });
    fields.push({ key: 'bonus', label: 'Bonus / Expectations', group: 'Compensation' });

    // Custom fields — dynamically loaded from DB
    if (customFieldDefs.length > 0) {
      customFieldDefs.forEach(cf => {
        fields.push({ key: `cf_${cf.field_key}`, label: `${cf.field_name}${cf.is_unique_key ? ' (ID key)' : ''}`, group: 'Custom Fields' });
      });
    }
    fields.push({ key: '__new_custom_field__', label: '➕ Create New Custom Field...', group: 'Custom Fields' });

    return fields;
  };

  const availableFields = buildAvailableFields();

  // ===== MANUAL ENTRY =====
  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { alert('Name is required'); return; }
    setSaving(true);
    try {
      const endpoint = userType === 'client'
        ? `${API_BASE}/api/client/candidates`
        : `${API_BASE}/api/candidates`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (!response.ok) { const d = await response.json(); throw new Error(d.error || 'Failed'); }
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error creating candidate:', error);
      alert(error.message);
    } finally { setSaving(false); }
  };

  // ===== FILE PARSING =====
  // XLSX is loaded lazily on first parse so the ~600kB library isn't
  // dragged into the main bundle just because this modal exists.
  // The first import takes a beat (file system / network), subsequent
  // calls are cached by webpack/module loader.
  const parseFile = useCallback((file) => {
    setUploadedFile(file);
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const XLSX = await import('xlsx');
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        // raw: false → returns formatted strings (not raw numbers/dates)
        // defval: '' → empty cells become '' instead of undefined
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' });

        if (jsonData.length < 2) { alert('File must have at least a header row and one data row'); return; }

        const headers = jsonData[0].map((h, i) => h?.toString() || `Column ${i + 1}`);
        const rows = jsonData.slice(1).filter(row => row.some(cell => cell != null && cell !== ''));

        setFileColumns(headers);
        setParsedData(rows);

        // Smart auto-map
        const autoMapping = {};
        const usedFields = new Set();

        headers.forEach((header, index) => {
          const h = header.toLowerCase().trim();
          const hClean = header.trim();
          let mapped = null;

          if (/^first[\s_-]?name$/i.test(hClean)) mapped = 'first_name';
          else if (/^last[\s_-]?name$/i.test(hClean)) mapped = 'last_name';
          else if (/^(full[\s_-]?)?name$/i.test(hClean) && !h.includes('company') && !h.includes('cert') && !h.includes('org')) mapped = 'name';
          else if (h.includes('email') || h.includes('e-mail')) mapped = 'email';
          else if (h.includes('phone') || h.includes('mobile') || h.includes('cell')) mapped = 'phone';
          else if (h.includes('linkedin')) mapped = 'linkedin';
          else if ((h.includes('title') || h === 'role' || h === 'job title') && !h.includes('degree') && !h.includes('position')) mapped = 'current_title';
          else if ((h.includes('company') || h.includes('employer') || h === 'organization') && !h.includes('cert') && !h.includes('issuing')) mapped = 'current_company';
          else if (h === 'location' || h === 'address' || h === 'full location' || h === 'location full') mapped = 'location';
          else if (h === 'city' || h === 'loc_city' || h === 'candidate city') mapped = 'loc_city';
          else if (h === 'state' || h === 'state/province' || h === 'province' || h === 'region' || h === 'loc_state') mapped = 'loc_state';
          else if (h === 'country' || h === 'nation' || h === 'loc_country') mapped = 'loc_country';
          else if (h === 'zip' || h === 'zip code' || h === 'zipcode' || h === 'postal code' || h === 'postal' || h === 'loc_zip') mapped = 'loc_zip';
          else if (h.includes('source') || h.includes('referral')) mapped = 'referral_source';
          else if (h === 'notes' || h === 'note' || h === 'comments') mapped = 'notes';
          else if (h === 'tags' || h === 'tag' || h === 'labels') mapped = 'tags_combined';
          else if ((h.includes('salary') || h.includes('base pay')) && h.includes('current')) mapped = 'current_base_salary';
          else if (h.includes('total comp') && h.includes('current')) mapped = 'current_total_comp';
          // Position-related headers — broad matching
          else if (/^(experience|work\s*history|employment\s*(history)?|positions?\s*(history)?|work\s*experience|career\s*(history)?|professional\s*(experience|history|background)|job\s*(history|experience)?|background|roles?|past\s*(roles?|positions?|jobs?)|work)$/i.test(h)) mapped = 'pos_combined';
          else if ((h.includes('previous') || h.includes('prior') || h.includes('past') || h.includes('former')) && (h.includes('company') || h.includes('employer'))) mapped = 'pos_1_company';
          else if ((h.includes('previous') || h.includes('prior') || h.includes('past') || h.includes('former')) && (h.includes('title') || h.includes('role') || h.includes('position'))) mapped = 'pos_1_title';
          // Education-related headers — broad matching
          else if (/^(education|education\s*history|academic\s*(background|history)|degrees?|qualifications?|schooling|studies)$/i.test(h)) mapped = 'edu_combined';
          else if (h === 'school' || h === 'university' || h === 'college' || h === 'institution') mapped = 'edu_1_institution';
          else if (h === 'degree' || h === 'degree type' || h === 'qualification') mapped = 'edu_1_degree';
          else if (h === 'major' || h === 'field of study' || h === 'field' || h === 'concentration' || h === 'subject') mapped = 'edu_1_field';
          // Certification-related headers — broad matching
          else if (/^(certifications?|certificates?|licenses?|credentials?|accreditations?)$/i.test(h)) mapped = 'cert_combined';
          else if (/^(certification|certificate|license|credential)\s*(name)?$/i.test(h)) mapped = 'cert_1_name';

          if (mapped && !usedFields.has(mapped)) { autoMapping[index] = mapped; usedFields.add(mapped); }
        });

        // Data-peek heuristic pass 1: reclassify columns mapped to simple fields that contain multi-entry data
        const sampleRows = rows.slice(0, Math.min(5, rows.length));
        Object.entries(autoMapping).forEach(([colIndex, fieldKey]) => {
          if (fieldKey === 'current_title' || fieldKey === 'current_company') {
            const samples = sampleRows.map(r => (r[parseInt(colIndex)] || '').toString()).filter(Boolean);
            const hasMultiEntry = samples.some(s => s.includes(';') || (s.match(/\bat\b/gi) || []).length > 1);
            const hasDatePattern = samples.some(s => /\d{1,2}\/\d{4}|\d{4}\s*(-|to)\s*(\d{4}|present)/i.test(s));
            if (hasMultiEntry || hasDatePattern) {
              if (!usedFields.has('pos_combined')) {
                usedFields.delete(fieldKey);
                autoMapping[colIndex] = 'pos_combined';
                usedFields.add('pos_combined');
              }
            }
          }
        });

        // Data-peek heuristic pass 2: check ALL UNMAPPED columns for position-like data
        // This catches columns with non-standard headers (e.g. "Position History", "Jobs", "Role Details")
        if (!usedFields.has('pos_combined')) {
          headers.forEach((header, index) => {
            if (autoMapping[index]) return; // Already mapped
            const samples = sampleRows.map(r => (r[parseInt(index)] || '').toString()).filter(Boolean);
            if (samples.length === 0) return;
            
            // Check if data looks like position entries
            const looksLikePositions = samples.some(s => {
              const hasAtPattern = /\bat\b/i.test(s) && s.length > 15;
              const hasDateRange = /\d{4}\s*(-|–|to)\s*(\d{4}|present|current)/i.test(s);
              const hasFromTo = /\bfrom\s+\d{4}/i.test(s);
              const hasSemicolonEntries = s.includes(';') && s.length > 30;
              const hasJobKeywords = /\b(manager|director|engineer|analyst|consultant|associate|vp|ceo|cfo|intern|coordinator|specialist)\b/i.test(s);
              return (hasDateRange || hasFromTo) && (hasAtPattern || hasJobKeywords || hasSemicolonEntries);
            });
            
            if (looksLikePositions) {
              autoMapping[index] = 'pos_combined';
              usedFields.add('pos_combined');
            }
          });
        }

        // Data-peek heuristic pass 3: check unmapped columns for education-like data
        if (!usedFields.has('edu_combined')) {
          headers.forEach((header, index) => {
            if (autoMapping[index]) return;
            const samples = sampleRows.map(r => (r[parseInt(index)] || '').toString()).filter(Boolean);
            if (samples.length === 0) return;
            
            const looksLikeEducation = samples.some(s => {
              const hasDegree = /\b(bachelor|master|mba|phd|bsc|msc|ba\b|bs\b|diploma|associate|doctorate|degree)\b/i.test(s);
              const hasSchool = /\b(university|college|school|institute|academy)\b/i.test(s);
              return hasDegree || (hasSchool && s.length > 10);
            });
            
            if (looksLikeEducation) {
              autoMapping[index] = 'edu_combined';
              usedFields.add('edu_combined');
            }
          });
        }

        setColumnMapping(autoMapping);
        setUploadStep('map');
      } catch (error) {
        console.error('Error parsing file:', error);
        alert('Error parsing file. Please ensure it is a valid CSV or Excel file.');
      }
    };
    reader.readAsArrayBuffer(file);
  }, []);

  const handleFileUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) parseFile(file);
  }, [parseFile]);

  // ===== DRAG AND DROP =====
  const handleDragEnter = useCallback((e) => { e.preventDefault(); e.stopPropagation(); dragCounter.current++; if (dragCounter.current === 1) setIsDragging(true); }, []);
  const handleDragLeave = useCallback((e) => { e.preventDefault(); e.stopPropagation(); dragCounter.current--; if (dragCounter.current === 0) setIsDragging(false); }, []);
  const handleDragOver = useCallback((e) => { e.preventDefault(); e.stopPropagation(); }, []);
  const handleDrop = useCallback((e) => {
    e.preventDefault(); e.stopPropagation(); dragCounter.current = 0; setIsDragging(false);
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      const ext = files[0].name.split('.').pop().toLowerCase();
      if (['csv', 'xlsx', 'xls'].includes(ext)) parseFile(files[0]);
      else alert('Please upload a CSV, XLS, or XLSX file.');
    }
  }, [parseFile]);

  // ===== COLUMN MAPPING =====
  const handleMappingChange = (columnIndex, fieldKey) => {
    // Intercept "Create New Custom Field" selection
    if (fieldKey === '__new_custom_field__') {
      setShowNewFieldForm(true);
      setNewFieldName('');
      setNewFieldIsKey(false);
      // Store which column triggered it so we can auto-map after creation
      setShowNewFieldForm(columnIndex);
      return;
    }
    setColumnMapping(prev => {
      const newMapping = { ...prev };
      Object.keys(newMapping).forEach(key => { if (newMapping[key] === fieldKey) delete newMapping[key]; });
      if (fieldKey) newMapping[columnIndex] = fieldKey;
      else delete newMapping[columnIndex];
      return newMapping;
    });
  };

  const handleCreateCustomField = async () => {
    if (!newFieldName.trim()) return;
    const created = await createCustomField(newFieldName.trim(), newFieldIsKey);
    if (created && showNewFieldForm !== false && showNewFieldForm !== true) {
      // Auto-map the column that triggered the creation
      const fieldKey = `cf_${created.field_key}`;
      setColumnMapping(prev => ({ ...prev, [showNewFieldForm]: fieldKey }));
    }
    setShowNewFieldForm(false);
    setNewFieldName('');
    setNewFieldIsKey(false);
  };
  const getMappedField = (columnIndex) => columnMapping[columnIndex] || '';
  const isNameMapped = Object.values(columnMapping).includes('name') || Object.values(columnMapping).includes('first_name');
  const isFieldUsed = (fieldKey, currentColIndex) => Object.entries(columnMapping).some(([idx, key]) => key === fieldKey && parseInt(idx) !== currentColIndex);
  const mappedCount = (prefix) => Object.values(columnMapping).filter(v => v.startsWith(prefix)).length;

  // ===== TAG RESOLUTION =====
  const hasTagColumns = Object.values(columnMapping).includes('tags_combined');
  const hasPosColumns = mappedCount('pos_') > 0 || Object.values(columnMapping).includes('pos_combined');
  const hasEduColumns = mappedCount('edu_') > 0 || Object.values(columnMapping).includes('edu_combined');
  const hasCertColumns = mappedCount('cert_') > 0 || Object.values(columnMapping).includes('cert_combined');
  const hasCustomFields = Object.values(columnMapping).some(v => v.startsWith('cf_'));
  const uniqueKeyFields = customFieldDefs.filter(cf => cf.is_unique_key);

  /**
   * Parse a single cell value into individual tag strings.
   * Handles: JSON arrays ["tag1","tag2"], comma-separated, semicolon-separated,
   * pipe-separated, and plain single values.
   */
  const parseTagCell = (val) => {
    if (!val) return [];
    const str = val.toString().trim();
    if (!str) return [];

    // Try JSON array first: ["tag1", "tag2"] or ['tag1', 'tag2']
    if (str.startsWith('[')) {
      try {
        const parsed = JSON.parse(str.replace(/'/g, '"'));
        if (Array.isArray(parsed)) {
          return parsed.map(t => (typeof t === 'string' ? t.trim() : String(t).trim())).filter(Boolean);
        }
      } catch (e) {
        // Strip brackets and treat as comma-separated
        const inner = str.replace(/^\[|\]$/g, '').replace(/['"]/g, '');
        return inner.split(/[,;|]/).map(t => t.trim()).filter(Boolean);
      }
    }

    // Split by common delimiters: comma, semicolon, pipe
    if (/[,;|]/.test(str)) {
      return str.split(/[,;|]/).map(t => t.trim()).filter(Boolean);
    }

    // Single value
    return [str];
  };

  /**
   * Load system tags from API and RETURN the data directly 
   * (avoids React setState race condition)
   */
  const loadSystemTags = async () => {
    setLoadingTags(true);
    try {
      const endpoint = userType === 'client'
        ? `${API_BASE}/api/client/tags`
        : `${API_BASE}/api/internal/tags`;
      console.log('[loadSystemTags] Fetching:', endpoint, 'userType:', userType);
      const resp = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      console.log('[loadSystemTags] Response status:', resp.status);
      if (resp.ok) {
        const data = await resp.json();
        console.log('[loadSystemTags] Got', data.length, 'tags:', data.map(t => t.name));
        setSystemTags(data);
        return data;
      } else {
        const errText = await resp.text();
        console.error('[loadSystemTags] Error response:', resp.status, errText);
      }
    } catch (err) { console.error('[loadSystemTags] Exception:', err); }
    finally { setLoadingTags(false); }
    return [];
  };

  /**
   * Extract ALL unique tag strings from ALL rows across ALL tag-mapped columns.
   * Uses parseTagCell to handle arrays and delimited values in any tag field.
   */

  // Load custom field definitions from API
  const loadCustomFieldDefs = async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/candidate-archive/custom-fields`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setCustomFieldDefs(data);
        return data;
      }
    } catch (err) { console.error('Error loading custom fields:', err); }
    return [];
  };

  // Create a new custom field definition
  const createCustomField = async (fieldName, isUniqueKey = false) => {
    try {
      const resp = await fetch(`${API_BASE}/api/candidate-archive/custom-fields`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ field_name: fieldName, field_type: 'text', is_unique_key: isUniqueKey })
      });
      if (resp.ok) {
        const newField = await resp.json();
        setCustomFieldDefs(prev => [...prev, newField]);
        return newField;
      }
    } catch (err) { console.error('Error creating custom field:', err); }
    return null;
  };

  // Load custom field defs when switching to upload tab
  useEffect(() => {
    if (activeTab === 'upload') loadCustomFieldDefs();
  }, [activeTab]);

  const extractUniqueTags = () => {
    const tagSet = new Set();
    if (!parsedData) return [];
    parsedData.forEach(row => {
      Object.entries(columnMapping).forEach(([colIndex, fieldKey]) => {
        if (fieldKey !== 'tags_combined' && !fieldKey.startsWith('tag_')) return;
        const val = row[parseInt(colIndex)];
        parseTagCell(val).forEach(t => tagSet.add(t));
      });
    });
    return Array.from(tagSet).sort((a, b) => a.localeCompare(b));
  };

  /**
   * Build auto-mappings by matching CSV tag names to loaded system tags.
   * Uses the loaded data directly (passed in) instead of React state.
   */
  const buildAutoMappings = (uniqueTags, loadedTags) => {
    const mappings = {};
    uniqueTags.forEach(csvTag => {
      const match = loadedTags.find(st => st.name.toLowerCase() === csvTag.toLowerCase());
      if (match) {
        mappings[csvTag] = { action: 'map', tagId: match.id, tagName: match.name, color: match.color || 'blue' };
      } else {
        mappings[csvTag] = { action: 'create', tagName: csvTag, color: 'blue' };
      }
    });
    return mappings;
  };

  const enterTagResolution = async () => {
    if (!hasTagColumns) { enterFirmResolution(); return; }
    setUploadStep('resolve-tags');

    // Load tags and get data directly (don't rely on state)
    const loadedTags = await loadSystemTags();
    const unique = extractUniqueTags();
    setUniqueCsvTags(unique);

    // Auto-match using the directly-returned data
    const autoMappings = buildAutoMappings(unique, loadedTags);
    setTagMappings(autoMappings);
  };

  // Extract unique company names from parsed CSV data
  const extractUniqueCompanies = () => {
    if (!parsedData) return [];
    const names = new Set();
    parsedData.forEach(row => {
      Object.entries(columnMapping).forEach(([colIndex, fieldKey]) => {
        const value = row[parseInt(colIndex)];
        if (!value) return;
        const v = value.toString().trim();
        if (fieldKey === 'pos_combined' && v) {
          const positions = parseMultiplePositions(v);
          positions.forEach(p => { if (p.company_name) names.add(p.company_name.trim()); });
        }
        if (fieldKey.match(/^pos_\d+_company$/) && v) names.add(v);
      });
    });
    return [...names].sort();
  };

  const enterFirmResolution = async () => {
    setUploadStep('resolve-firms');
    // Load existing firms
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE}/api/firms`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const firms = await res.json();
        setAllFirmsForUpload(firms);
        // Extract unique companies and auto-match
        const companies = extractUniqueCompanies();
        setUniqueCompanies(companies);
        const mappings = {};
        companies.forEach(name => {
          const match = firms.find(f => f.name.toLowerCase() === name.toLowerCase());
          if (match) {
            mappings[name] = { action: 'map', firmId: match.id, standardName: match.name };
          } else {
            mappings[name] = { action: 'create', firmId: null, standardName: name };
          }
        });
        setFirmMappings(mappings);
      }
    } catch (e) { console.log('Failed to load firms for mapping:', e); }
  };

  const tagColors = [
    { name: 'blue', bg: 'bg-blue-100', text: 'text-blue-700' },
    { name: 'green', bg: 'bg-green-100', text: 'text-green-700' },
    { name: 'purple', bg: 'bg-purple-100', text: 'text-purple-700' },
    { name: 'yellow', bg: 'bg-yellow-100', text: 'text-yellow-700' },
    { name: 'pink', bg: 'bg-pink-100', text: 'text-pink-700' },
    { name: 'orange', bg: 'bg-orange-100', text: 'text-orange-700' },
    { name: 'red', bg: 'bg-red-100', text: 'text-red-700' },
    { name: 'teal', bg: 'bg-teal-100', text: 'text-teal-700' },
    { name: 'gray', bg: 'bg-gray-100', text: 'text-gray-700' },
  ];
  const getTagColor = (c) => tagColors.find(tc => tc.name === c) || tagColors[0];

  // ===== PREVIEW DATA =====
  const getPreviewData = () => {
    if (!parsedData) return [];
    return parsedData.slice(0, 5).map(row => {
      const mappedRow = {};
      Object.entries(columnMapping).forEach(([colIndex, fieldKey]) => { mappedRow[fieldKey] = row[parseInt(colIndex)] || ''; });
      if (!mappedRow.name && (mappedRow.first_name || mappedRow.last_name)) {
        mappedRow.name = `${mappedRow.first_name || ''} ${mappedRow.last_name || ''}`.trim();
      }
      // Combine location sub-fields into display string
      if (!mappedRow.location && (mappedRow.loc_city || mappedRow.loc_state || mappedRow.loc_country)) {
        mappedRow.location = [mappedRow.loc_city, mappedRow.loc_state, mappedRow.loc_country].filter(Boolean).join(', ');
      }
      return mappedRow;
    });
  };

  const hasLocationMapping = Object.values(columnMapping).includes('location') || 
    Object.values(columnMapping).some(v => v.startsWith('loc_'));
  const previewFields = ['name', 'email', 'phone', 'current_title', 'current_company', 'location']
    .filter(f => {
      if (f === 'name') return true;
      if (f === 'location') return hasLocationMapping;
      return Object.values(columnMapping).includes(f);
    });
  if (!previewFields.includes('name')) previewFields.unshift('name');

  // ===== BATCH IMPORT =====
  const handleImport = async () => {
    if (!isNameMapped) { alert('Please map either "Full Name" or "First Name" column'); return; }

    setUploadStep('importing');
    setImportProgress({ current: 0, total: parsedData.length });
    setImportResults(null);

    const candidates = parsedData.map(row => {
      const mapped = {};
      Object.entries(columnMapping).forEach(([colIndex, fieldKey]) => {
        const value = row[parseInt(colIndex)];
        if (value != null && value !== '') mapped[fieldKey] = value.toString().trim();
      });

      let name = mapped.name;
      if (!name && (mapped.first_name || mapped.last_name)) name = `${mapped.first_name || ''} ${mapped.last_name || ''}`.trim();
      if (!name) return null;

      // Build resolved tags - use tag mappings
      const tags = [];
      const rawTags = [];
      if (mapped.tags_combined) parseTagCell(mapped.tags_combined).forEach(t => rawTags.push(t));

      rawTags.forEach(rt => {
        const mapping = tagMappings[rt];
        if (!mapping || mapping.action === 'skip') return;
        if (mapping.action === 'map') {
          tags.push({ id: mapping.tagId, name: mapping.tagName });
        } else {
          tags.push({ name: mapping.tagName || rt, color: mapping.color || 'blue', create: true });
        }
      });

      // Build positions (combined field + structured + free-text)
      const positions = [];
      
      // First: parse combined field (may contain multiple positions)
      if (mapped.pos_combined) {
        const multiPositions = parseMultiplePositions(mapped.pos_combined);
        positions.push(...multiPositions);
      }
      
      // Then: per-group position fields
      for (let i = 1; i <= positionGroupCount; i++) {
        // Try free-text first
        if (mapped[`pos_${i}_text`]) {
          const parsed = parsePositionText(mapped[`pos_${i}_text`]);
          if (parsed) positions.push(parsed);
        }
        // Then structured (can override or supplement)
        const pos = {
          company_name: mapped[`pos_${i}_company`] || null,
          job_title: mapped[`pos_${i}_title`] || null,
          start_date: normalizeDate(mapped[`pos_${i}_start`]) || null,
          end_date: normalizeDate(mapped[`pos_${i}_end`]) || null,
          is_current: i === 1 && !mapped[`pos_${i}_end`]
        };
        if (pos.company_name || pos.job_title) {
          // If we already have a free-text parsed entry for this group, merge
          if (mapped[`pos_${i}_text`] && positions.length > 0) {
            const last = positions[positions.length - 1];
            if (pos.company_name) last.company_name = pos.company_name;
            if (pos.job_title) last.job_title = pos.job_title;
            if (pos.start_date) last.start_date = pos.start_date;
            if (pos.end_date) last.end_date = pos.end_date;
          } else {
            positions.push(pos);
          }
        }
      }

      // Apply firm name standardization from resolve-firms step
      positions.forEach(pos => {
        if (pos.company_name && firmMappings[pos.company_name]) {
          const fm = firmMappings[pos.company_name];
          pos.company_name = fm.standardName || pos.company_name;
          if (fm.firmId) pos.firm_id = fm.firmId;
        }
      });

      // Build education (combined field + structured + free-text)
      const education = [];
      
      // First: parse combined field (may contain multiple entries)
      if (mapped.edu_combined) {
        const multiEdu = parseMultipleEducation(mapped.edu_combined);
        education.push(...multiEdu);
      }
      
      // Then: per-group education fields
      for (let i = 1; i <= educationGroupCount; i++) {
        if (mapped[`edu_${i}_text`]) {
          const parsed = parseEducationText(mapped[`edu_${i}_text`]);
          if (parsed) education.push(parsed);
        }
        const edu = {
          institution: mapped[`edu_${i}_institution`] || null,
          degree_type: mapped[`edu_${i}_degree`] || null,
          field_of_study: mapped[`edu_${i}_field`] || null,
          start_date: normalizeDate(mapped[`edu_${i}_start`]) || null,
          end_date: normalizeDate(mapped[`edu_${i}_end`]) || null
        };
        if (edu.institution || edu.degree_type) {
          if (mapped[`edu_${i}_text`] && education.length > 0) {
            const last = education[education.length - 1];
            if (edu.institution) last.institution = edu.institution;
            if (edu.degree_type) last.degree_type = edu.degree_type;
            if (edu.field_of_study) last.field_of_study = edu.field_of_study;
            if (edu.start_date) last.start_date = edu.start_date;
            if (edu.end_date) last.end_date = edu.end_date;
          } else {
            education.push(edu);
          }
        }
      }

      // Build certifications (combined field + structured)
      const certifications = [];
      
      // First: parse combined field
      if (mapped.cert_combined) {
        const multiCerts = parseMultipleCertifications(mapped.cert_combined);
        certifications.push(...multiCerts);
      }
      
      // Then: per-group cert fields
      for (let i = 1; i <= certGroupCount; i++) {
        const cert = {
          certification_name: mapped[`cert_${i}_name`] || null,
          issuing_organization: mapped[`cert_${i}_org`] || null,
          issue_date: normalizeDate(mapped[`cert_${i}_date`]) || null,
          expiration_date: normalizeDate(mapped[`cert_${i}_exp`]) || null
        };
        if (cert.certification_name) certifications.push(cert);
      }

      // Build location from sub-fields or single location field
      let locationString = mapped.location || null;
      let locationParts = null;
      
      if (mapped.loc_city || mapped.loc_state || mapped.loc_country || mapped.loc_zip) {
        locationParts = {
          city: mapped.loc_city || null,
          state: mapped.loc_state || null,
          country: mapped.loc_country || null,
          zip: mapped.loc_zip || null
        };
        // Build a display string from parts if no full location was mapped
        if (!locationString) {
          locationString = [mapped.loc_city, mapped.loc_state, mapped.loc_country].filter(Boolean).join(', ');
        }
      }

      // Auto-populate current_title/current_company from first (most recent) position if not mapped
      let currentTitle = mapped.current_title || null;
      let currentCompany = mapped.current_company || null;
      if (positions.length > 0 && (!currentTitle || !currentCompany)) {
        const firstPos = positions[0];
        if (!currentTitle && firstPos.job_title) currentTitle = firstPos.job_title;
        if (!currentCompany && firstPos.company_name) currentCompany = firstPos.company_name;
      }

      // Collect custom field values
      const custom_fields = {};
      Object.entries(mapped).forEach(([key, value]) => {
        if (key.startsWith('cf_') && value) {
          const fieldKey = key.substring(3); // Remove 'cf_' prefix
          custom_fields[fieldKey] = value;
        }
      });

      return {
        name, email: mapped.email || null, phone: mapped.phone || null, linkedin: mapped.linkedin || null,
        current_title: currentTitle, current_company: currentCompany,
        location: locationString, location_parts: locationParts,
        referral_source: mapped.referral_source || null, notes: mapped.notes || null,
        tags, positions, education, certifications,
        custom_fields: Object.keys(custom_fields).length > 0 ? custom_fields : null,
        current_base_salary: mapped.current_base_salary || null, current_total_comp: mapped.current_total_comp || null,
        expected_base: mapped.expected_base || null, bonus: mapped.bonus || null
      };
    }).filter(Boolean);

    if (candidates.length === 0) { alert('No valid candidates found'); setUploadStep('preview'); return; }

    try {
      const endpoint = userType === 'client'
        ? `${API_BASE}/api/candidate-archive/client-import`
        : `${API_BASE}/api/candidate-archive/internal-archive/import`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ candidates, dedup_field: dedupField === 'none' ? null : (dedupField === 'name' ? '__name__' : dedupField) })
      });

      if (!response.ok) {
        const text = await response.text();
        let errMsg = 'Import failed';
        try { errMsg = JSON.parse(text).error || errMsg; } catch (e) { errMsg = `Server error (${response.status})`; }
        throw new Error(errMsg);
      }

      const result = await response.json();
      setImportProgress({ current: candidates.length, total: candidates.length });
      setImportResults(result);
      if ((result.success > 0) || (result.merged > 0)) onSuccess();
      if (result.errors.length === 0) setTimeout(() => onClose(), 1500);
      else setUploadStep('preview');
    } catch (error) {
      console.error('Error importing:', error);
      alert('Import failed: ' + error.message);
      setUploadStep('preview');
    }
  };

  // Reset
  const resetUpload = () => {
    setUploadedFile(null); setParsedData(null); setFileColumns([]); setColumnMapping({});
    setUploadStep('upload'); setImportResults(null); setPositionGroupCount(1); setEducationGroupCount(1);
    setCertGroupCount(1); setTagSlotCount(3); setUniqueCsvTags([]); setTagMappings({}); setSystemTags([]);
    setUniqueCompanies([]); setFirmMappings({}); setAllFirmsForUpload([]); setFirmSearches({});
    dragCounter.current = 0; setIsDragging(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Group fields for dropdown
  const getGroupedFields = () => {
    const groups = {};
    availableFields.forEach(f => { if (!groups[f.group]) groups[f.group] = []; groups[f.group].push(f); });
    return groups;
  };
  const groupedFields = getGroupedFields();

  // ===== RENDER =====
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Add Candidates</h2>
            <p className="text-xs text-gray-500 mt-0.5">{userType === 'client' ? 'Adding to client database' : 'Adding to recruiter database'}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b flex-shrink-0">
          <div className="flex">
            {['manual', 'upload'].map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${activeTab === tab ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`}>
                {tab === 'manual' ? 'Manual Entry' : 'Upload CSV/Excel'}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {activeTab === 'manual' ? (
            <form onSubmit={handleManualSubmit} className="space-y-4">
              {[{ n: 'name', l: 'Name', r: true, p: 'John Smith', af: true },
                { n: 'email', l: 'Email', t: 'email', p: 'john@example.com' },
                { n: 'phone', l: 'Phone', t: 'tel', p: '+1 (555) 123-4567' },
                { n: 'linkedin', l: 'LinkedIn URL', t: 'url', p: 'https://linkedin.com/in/johnsmith' }
              ].map(f => (
                <div key={f.n}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{f.l} {f.r && <span className="text-red-500">*</span>}</label>
                  <input type={f.t || 'text'} name={f.n} value={formData[f.n]} onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" placeholder={f.p} autoFocus={f.af} />
                </div>
              ))}
              <div className="flex gap-3 pt-4">
                <button type="submit" disabled={saving || !formData.name.trim()} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 font-medium">
                  {saving ? 'Creating...' : 'Create Candidate'}
                </button>
                <button type="button" onClick={onClose} className="px-4 py-2 border text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              {/* STEP: UPLOAD */}
              {uploadStep === 'upload' && (
                <div
                  onDragEnter={handleDragEnter} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`}>
                  <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleFileUpload} className="hidden" />
                  <svg className={`w-12 h-12 mx-auto mb-3 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className={`font-medium ${isDragging ? 'text-blue-600' : 'text-gray-600'}`}>{isDragging ? 'Drop file here' : 'Click to upload or drag and drop'}</p>
                  <p className="text-sm text-gray-400 mt-1">CSV, XLS, or XLSX files</p>
                </div>
              )}

              {/* STEP: MAP COLUMNS */}
              {uploadStep === 'map' && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-medium text-gray-900">Map Columns</h3>
                      <p className="text-sm text-gray-500">{uploadedFile?.name} &bull; {parsedData?.length} rows detected</p>
                    </div>
                    <button onClick={resetUpload} className="text-sm text-gray-500 hover:text-gray-700">Upload different file</button>
                  </div>

                  {/* Group add buttons */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <button onClick={() => setPositionGroupCount(c => c + 1)} className="text-xs px-3 py-1.5 bg-purple-50 text-purple-700 rounded-full hover:bg-purple-100 border border-purple-200 font-medium">+ Position Group ({positionGroupCount})</button>
                    <button onClick={() => setEducationGroupCount(c => c + 1)} className="text-xs px-3 py-1.5 bg-green-50 text-green-700 rounded-full hover:bg-green-100 border border-green-200 font-medium">+ Education Group ({educationGroupCount})</button>
                    <button onClick={() => setCertGroupCount(c => c + 1)} className="text-xs px-3 py-1.5 bg-orange-50 text-orange-700 rounded-full hover:bg-orange-100 border border-orange-200 font-medium">+ Certification Group ({certGroupCount})</button>
                  </div>

                  {/* Name hint */}
                  {Object.values(columnMapping).includes('first_name') && !Object.values(columnMapping).includes('name') && (
                    <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                      <strong>First Name mapped</strong> — will be combined with Last Name into Full Name.
                      {!Object.values(columnMapping).includes('last_name') && <span className="ml-1 text-blue-600">Consider also mapping "Last Name".</span>}
                    </div>
                  )}

                  {/* Smart parsing hints */}
                  {(hasPosColumns || hasEduColumns || hasCertColumns) && (
                    Object.values(columnMapping).some(v => v.endsWith('_text') || v.endsWith('_combined'))
                  ) && (
                    <div className="mb-3 px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-lg text-sm text-indigo-800">
                      <strong>Smart parsing enabled.</strong> Sentence-format and combined columns will be auto-parsed into structured data:
                      {hasPosColumns && <span className="block mt-1">• <strong>Positions:</strong> "VP at Goldman Sachs, Jan 2018 - Present" → title, company, dates. Semicolons or line breaks separate multiple entries.</span>}
                      {hasEduColumns && <span className="block mt-1">• <strong>Education:</strong> "Bachelor of Science (BSc), Economics, Swansea University, 1996 to 1999" → degree, field, institution, dates.</span>}
                      {hasCertColumns && <span className="block mt-1">• <strong>Certifications:</strong> "AWS Solutions Architect, Amazon, 2022" → cert name, org, date.</span>}
                    </div>
                  )}

                  {/* Mapping table */}
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left font-medium text-gray-600 w-1/4">File Column</th>
                          <th className="px-4 py-2 text-left font-medium text-gray-600 w-1/4">Sample Data</th>
                          <th className="px-4 py-2 text-left font-medium text-gray-600 w-1/2">Map To</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {fileColumns.map((column, index) => {
                          const cur = getMappedField(index);
                          const mappingColor = cur
                            ? (cur === 'name' || cur === 'first_name' || cur === 'last_name' ? 'border-green-500 bg-green-50'
                              : cur.startsWith('pos_') ? 'border-purple-400 bg-purple-50'
                              : cur.startsWith('edu_') ? 'border-emerald-400 bg-emerald-50'
                              : cur.startsWith('cert_') ? 'border-orange-400 bg-orange-50'
                              : cur.startsWith('tag') ? 'border-blue-400 bg-blue-50'
                              : cur.includes('base') || cur.includes('total') || cur.includes('expected') || cur === 'bonus' ? 'border-yellow-400 bg-yellow-50'
                              : 'border-gray-400 bg-gray-50')
                            : 'border-gray-300';
                          return (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-4 py-2 font-medium text-gray-900">{column}</td>
                              <td className="px-4 py-2 text-gray-500 truncate max-w-[150px]">{parsedData?.[0]?.[index]?.toString() || '\u2014'}</td>
                              <td className="px-4 py-2">
                                <select value={cur} onChange={(e) => handleMappingChange(index, e.target.value)} className={`w-full px-2 py-1 border rounded text-sm ${mappingColor}`}>
                                  <option value="">{'\u2014'} Don't import {'\u2014'}</option>
                                  {Object.entries(groupedFields).map(([groupName, fields]) => (
                                    <optgroup key={groupName} label={groupName}>
                                      {fields.map(field => (
                                        <option key={field.key} value={field.key} disabled={isFieldUsed(field.key, index)}>
                                          {field.label} {field.required ? '*' : ''}
                                        </option>
                                      ))}
                                    </optgroup>
                                  ))}
                                </select>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Summary chips */}
                  <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    {Object.values(columnMapping).length > 0 && (
                      <>
                        <span className="px-2 py-1 bg-gray-100 rounded text-gray-600">{Object.values(columnMapping).length} columns mapped</span>
                        {hasPosColumns && <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">positions</span>}
                        {hasEduColumns && <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded">education</span>}
                        {hasCertColumns && <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded">certifications</span>}
                        {hasTagColumns && <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">tags</span>}
                      </>
                    )}
                  </div>

                  {!isNameMapped && <p className="text-sm text-red-500 mt-2">Please map a column to "Full Name" or "First Name" (required)</p>}

                  {/* Create Custom Field inline form */}
                  {showNewFieldForm !== false && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm font-medium text-blue-900 mb-2">Create New Custom Field</p>
                      <div className="flex gap-2 items-center">
                        <input type="text" value={newFieldName} onChange={e => setNewFieldName(e.target.value)} placeholder="Field name (e.g. ATS ID, Source System)"
                          className="flex-1 px-3 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500" autoFocus onKeyDown={e => e.key === 'Enter' && handleCreateCustomField()} />
                        <label className="flex items-center gap-1 text-xs text-blue-700 whitespace-nowrap">
                          <input type="checkbox" checked={newFieldIsKey} onChange={e => setNewFieldIsKey(e.target.checked)} className="rounded" />
                          Unique ID key
                        </label>
                        <button onClick={handleCreateCustomField} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">Create</button>
                        <button onClick={() => setShowNewFieldForm(false)} className="px-3 py-1.5 border text-gray-600 text-sm rounded hover:bg-gray-50">Cancel</button>
                      </div>
                      <p className="text-xs text-blue-600 mt-1">Check "Unique ID key" if this field contains a unique identifier (like an ATS ID) that can be used to match existing candidates on re-upload.</p>
                    </div>
                  )}

                  {/* Duplicate handling */}
                  <div className="mt-3 p-3 bg-gray-50 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Duplicate Handling</p>
                        <p className="text-xs text-gray-500">{dedupField === 'none' ? 'Each row creates a new candidate.' : 'Matching candidates will be merged instead of duplicated.'}</p>
                      </div>
                      <select value={dedupField} onChange={e => setDedupField(e.target.value)} className="px-2 py-1 text-sm border rounded bg-white">
                        <option value="none">No matching (always create new)</option>
                        <option value="name">Match by Full Name</option>
                        {uniqueKeyFields.map(cf => (
                          <option key={cf.field_key} value={cf.field_key}>Match by {cf.field_name}</option>
                        ))}
                        {customFieldDefs.filter(cf => !cf.is_unique_key).map(cf => (
                          <option key={cf.field_key} value={cf.field_key}>Match by {cf.field_name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button onClick={() => enterTagResolution()} disabled={!isNameMapped}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium">
                      {hasTagColumns ? 'Next: Resolve Tags' : 'Next: Company Names'}
                    </button>
                    <button onClick={resetUpload} className="px-4 py-2 border text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
                  </div>
                </div>
              )}

              {/* STEP: RESOLVE TAGS */}
              {uploadStep === 'resolve-tags' && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-medium text-gray-900">Resolve Tags</h3>
                      <p className="text-sm text-gray-500">
                        {uniqueCsvTags.length} unique tag{uniqueCsvTags.length !== 1 ? 's' : ''} found across all candidates.
                        {systemTags.length > 0
                          ? ` ${systemTags.length} existing tag${systemTags.length !== 1 ? 's' : ''} available to map to.`
                          : ' No existing tags found — all will be created as new.'}
                      </p>
                    </div>
                    <button onClick={() => setUploadStep('map')} className="text-sm text-blue-600 hover:text-blue-800">← Edit mapping</button>
                  </div>

                  {loadingTags ? (
                    <div className="py-8 text-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div><p className="text-sm text-gray-500 mt-2">Loading existing tags...</p></div>
                  ) : uniqueCsvTags.length === 0 ? (
                    <div className="py-6 text-center text-sm text-gray-500">No tag values found in mapped columns.</div>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left font-medium text-gray-600">CSV Tag Value</th>
                            <th className="px-4 py-2 text-left font-medium text-gray-600">Action</th>
                            <th className="px-4 py-2 text-left font-medium text-gray-600">Maps To</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {uniqueCsvTags.map(csvTag => {
                            const mapping = tagMappings[csvTag] || { action: 'create', tagName: csvTag, color: 'blue' };
                            // Count how many rows in the CSV contain this tag
                            let tagOccurrences = 0;
                            if (parsedData) {
                              parsedData.forEach(row => {
                                Object.entries(columnMapping).forEach(([colIndex, fieldKey]) => {
                                  if (fieldKey !== 'tags_combined' && !fieldKey.startsWith('tag_')) return;
                                  const parsed = parseTagCell(row[parseInt(colIndex)]);
                                  if (parsed.some(t => t === csvTag)) tagOccurrences++;
                                });
                              });
                            }
                            return (
                              <tr key={csvTag} className="hover:bg-gray-50">
                                <td className="px-4 py-2">
                                  <div className="flex items-center gap-2">
                                    <span className="px-2 py-0.5 bg-gray-100 rounded text-gray-800 text-xs font-medium">{csvTag}</span>
                                    {tagOccurrences > 0 && (
                                      <span className="text-[10px] text-gray-400">{tagOccurrences} candidate{tagOccurrences !== 1 ? 's' : ''}</span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-2">
                                  <select
                                    value={mapping.action}
                                    onChange={(e) => {
                                      const action = e.target.value;
                                      if (action === 'skip') {
                                        setTagMappings(prev => ({ ...prev, [csvTag]: { action: 'skip' } }));
                                      } else if (action === 'create') {
                                        setTagMappings(prev => ({ ...prev, [csvTag]: { action: 'create', tagName: csvTag, color: 'blue' } }));
                                      } else {
                                        // Find best matching system tag by name, or fall back to first
                                        const bestMatch = systemTags.find(st => st.name.toLowerCase() === csvTag.toLowerCase()) || systemTags[0];
                                        if (bestMatch) {
                                          setTagMappings(prev => ({ ...prev, [csvTag]: { action: 'map', tagId: bestMatch.id, tagName: bestMatch.name, color: bestMatch.color || 'blue' } }));
                                        }
                                      }
                                    }}
                                    className="px-2 py-1 border rounded text-xs"
                                  >
                                    {systemTags.length > 0 && <option value="map">Map to existing</option>}
                                    <option value="create">Create new</option>
                                    <option value="skip">Skip (don't import)</option>
                                  </select>
                                </td>
                                <td className="px-4 py-2">
                                  {mapping.action === 'map' && (
                                    <select
                                      value={mapping.tagId || ''}
                                      onChange={(e) => {
                                        const tag = systemTags.find(t => String(t.id) === e.target.value);
                                        if (tag) setTagMappings(prev => ({ ...prev, [csvTag]: { action: 'map', tagId: tag.id, tagName: tag.name, color: tag.color || 'blue' } }));
                                      }}
                                      className="px-2 py-1 border rounded text-xs w-full"
                                    >
                                      <option value="">Select tag...</option>
                                      {systemTags.map(st => (
                                        <option key={st.id} value={String(st.id)}>{st.name}</option>
                                      ))}
                                    </select>
                                  )}
                                  {mapping.action === 'create' && (
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="text"
                                        value={mapping.tagName || ''}
                                        onChange={(e) => setTagMappings(prev => ({ ...prev, [csvTag]: { ...prev[csvTag], tagName: e.target.value } }))}
                                        className="flex-1 px-2 py-1 border rounded text-xs"
                                        placeholder="Tag name"
                                      />
                                      <div className="flex gap-0.5">
                                        {tagColors.slice(0, 5).map(c => (
                                          <button key={c.name} onClick={() => setTagMappings(prev => ({ ...prev, [csvTag]: { ...prev[csvTag], color: c.name } }))}
                                            className={`w-4 h-4 rounded-full ${c.bg} border ${mapping.color === c.name ? 'ring-2 ring-blue-500 ring-offset-1' : 'border-gray-300'}`} />
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {mapping.action === 'skip' && <span className="text-xs text-gray-400 italic">Skipped</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Tag summary */}
                  <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    {Object.values(tagMappings).filter(m => m.action === 'map').length > 0 && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded">{Object.values(tagMappings).filter(m => m.action === 'map').length} mapped to existing</span>
                    )}
                    {Object.values(tagMappings).filter(m => m.action === 'create').length > 0 && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">{Object.values(tagMappings).filter(m => m.action === 'create').length} will be created</span>
                    )}
                    {Object.values(tagMappings).filter(m => m.action === 'skip').length > 0 && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-500 rounded">{Object.values(tagMappings).filter(m => m.action === 'skip').length} skipped</span>
                    )}
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button onClick={() => enterFirmResolution()} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">Next: Company Names</button>
                    <button onClick={() => setUploadStep('map')} className="px-4 py-2 border text-gray-700 rounded-lg hover:bg-gray-50">Back</button>
                  </div>
                </div>
              )}

              {/* STEP: RESOLVE FIRMS */}
              {uploadStep === 'resolve-firms' && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-medium text-gray-900">Standardize Company Names</h3>
                      <p className="text-sm text-gray-500">{uniqueCompanies.length} unique company name{uniqueCompanies.length !== 1 ? 's' : ''} found in positions data</p>
                    </div>
                    <button onClick={() => hasTagColumns ? setUploadStep('resolve-tags') : setUploadStep('map')} className="text-sm text-blue-600 hover:text-blue-800">← Edit {hasTagColumns ? 'tags' : 'mapping'}</button>
                  </div>

                  {uniqueCompanies.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-sm text-gray-500">No company names found in position data</p>
                      <p className="text-xs text-gray-400 mt-1">You can skip this step</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                      {uniqueCompanies.map(name => {
                        const mapping = firmMappings[name] || { action: 'create', standardName: name };
                        const isMatched = mapping.action === 'map';
                        const searchText = firmSearches[name] || '';
                        const showDropdown = searchText.length > 0;
                        const filtered = allFirmsForUpload.filter(f => 
                          f.name.toLowerCase().includes(searchText.toLowerCase()) && f.name.toLowerCase() !== name.toLowerCase()
                        ).slice(0, 5);

                        return (
                          <div key={name} className={`border rounded-lg p-3 ${isMatched ? 'bg-green-50 border-green-200' : 'bg-white'}`}>
                            <div className="flex items-center gap-3">
                              {/* CSV company name */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className={`text-sm font-medium ${isMatched ? 'text-green-800' : 'text-gray-900'}`}>{name}</span>
                                  {isMatched ? (
                                    <span className="text-[9px] font-medium text-green-700 bg-green-100 px-1.5 py-0.5 rounded border border-green-200">
                                      → {mapping.standardName}
                                    </span>
                                  ) : (
                                    <span className="text-[9px] font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200">New firm</span>
                                  )}
                                </div>
                              </div>

                              {/* Standard name edit */}
                              <div className="relative w-48">
                                <input type="text"
                                  value={mapping.standardName}
                                  onChange={e => {
                                    const val = e.target.value;
                                    setFirmMappings(prev => ({ ...prev, [name]: { ...prev[name], standardName: val } }));
                                    setFirmSearches(prev => ({ ...prev, [name]: val }));
                                  }}
                                  onFocus={() => setFirmSearches(prev => ({ ...prev, [name]: mapping.standardName }))}
                                  onBlur={() => setTimeout(() => setFirmSearches(prev => ({ ...prev, [name]: '' })), 200)}
                                  className="w-full px-2.5 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                  placeholder="Standard name..."
                                />
                                {showDropdown && filtered.length > 0 && (
                                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-32 overflow-y-auto">
                                    {filtered.map(f => (
                                      <button key={f.id} type="button"
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          setFirmMappings(prev => ({ ...prev, [name]: { action: 'map', firmId: f.id, standardName: f.name } }));
                                          setFirmSearches(prev => ({ ...prev, [name]: '' }));
                                        }}
                                        className="w-full px-2.5 py-1.5 text-left text-xs hover:bg-blue-50 flex items-center gap-1.5">
                                        <svg className="w-3 h-3 text-blue-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                                        {f.name}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* Reset if mapped */}
                              {isMatched && (
                                <button onClick={() => setFirmMappings(prev => ({ ...prev, [name]: { action: 'create', firmId: null, standardName: name } }))}
                                  className="text-[10px] text-gray-400 hover:text-red-500 p-1">
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="flex gap-3 pt-4">
                    <button onClick={() => setUploadStep('preview')} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">Preview Import</button>
                    <button onClick={() => hasTagColumns ? setUploadStep('resolve-tags') : setUploadStep('map')} className="px-4 py-2 border text-gray-700 rounded-lg hover:bg-gray-50">Back</button>
                  </div>
                </div>
              )}

              {/* STEP: PREVIEW */}
              {uploadStep === 'preview' && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-medium text-gray-900">Preview Import</h3>
                      <p className="text-sm text-gray-500">{parsedData?.length} candidates will be imported {userType === 'client' ? 'to client database' : 'to recruiter database'}</p>
                    </div>
                    <button onClick={() => setUploadStep('resolve-firms')} className="text-sm text-blue-600 hover:text-blue-800">← Edit company names</button>
                  </div>

                  {importResults && (
                    <div className={`mb-4 p-3 rounded-lg border ${importResults.errors.length > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'}`}>
                      <p className="font-medium text-sm">
                        {importResults.success > 0 && <span className="text-green-700">&#10003; {importResults.success} imported successfully{importResults.merged > 0 ? ` (${importResults.merged} merged with existing)` : ''}. </span>}
                        {importResults.success === 0 && importResults.merged > 0 && <span className="text-blue-700">&#10003; {importResults.merged} merged with existing candidates. </span>}
                        {importResults.errors.length > 0 && <span className="text-red-700">{importResults.errors.length} errors:</span>}
                      </p>
                      {importResults.errors.length > 0 && (
                        <ul className="text-sm text-red-600 mt-1 max-h-32 overflow-auto">
                          {importResults.errors.map((err, i) => <li key={i}>Row {err.row}: {err.name ? `${err.name} \u2014 ` : ''}{err.error}</li>)}
                        </ul>
                      )}
                    </div>
                  )}

                  <div className="border rounded-lg overflow-auto max-h-64">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          {previewFields.map(key => (
                            <th key={key} className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">
                              {key === 'name' ? 'Name' : key === 'current_title' ? 'Title' : key === 'current_company' ? 'Company' : key.charAt(0).toUpperCase() + key.slice(1)}
                            </th>
                          ))}
                          {hasTagColumns && <th className="px-3 py-2 text-left font-medium text-gray-600">Tags</th>}
                          {hasPosColumns && <th className="px-3 py-2 text-left font-medium text-gray-600">Positions</th>}
                          {hasEduColumns && <th className="px-3 py-2 text-left font-medium text-gray-600">Education</th>}
                          {hasCertColumns && <th className="px-3 py-2 text-left font-medium text-gray-600">Certs</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {getPreviewData().map((row, i) => {
                          // Parse tags
                          let tagCount = 0;
                          if (row.tags_combined) tagCount += row.tags_combined.split(',').filter(t => t.trim()).length;
                          
                          // Parse positions for this row
                          const parsedPositions = [];
                          if (row.pos_combined) parsedPositions.push(...parseMultiplePositions(row.pos_combined));
                          for (let p = 1; p <= positionGroupCount; p++) {
                            if (row[`pos_${p}_text`]) { const parsed = parsePositionText(row[`pos_${p}_text`]); if (parsed) parsedPositions.push(parsed); }
                            else if (row[`pos_${p}_company`] || row[`pos_${p}_title`]) parsedPositions.push({ job_title: row[`pos_${p}_title`], company_name: row[`pos_${p}_company`] });
                          }
                          
                          // Parse education for this row
                          const parsedEducation = [];
                          if (row.edu_combined) parsedEducation.push(...parseMultipleEducation(row.edu_combined));
                          for (let e = 1; e <= educationGroupCount; e++) {
                            if (row[`edu_${e}_text`]) { const parsed = parseEducationText(row[`edu_${e}_text`]); if (parsed) parsedEducation.push(parsed); }
                            else if (row[`edu_${e}_institution`] || row[`edu_${e}_degree`]) parsedEducation.push({ institution: row[`edu_${e}_institution`], degree_type: row[`edu_${e}_degree`], field_of_study: row[`edu_${e}_field`] });
                          }
                          
                          // Parse certs for this row
                          const parsedCerts = [];
                          if (row.cert_combined) parsedCerts.push(...parseMultipleCertifications(row.cert_combined));
                          for (let c = 1; c <= certGroupCount; c++) {
                            if (row[`cert_${c}_name`]) parsedCerts.push({ certification_name: row[`cert_${c}_name`], issuing_organization: row[`cert_${c}_org`] });
                          }

                          return (
                            <tr key={i} className="hover:bg-gray-50 align-top">
                              {previewFields.map(key => (
                                <td key={key} className="px-3 py-2 text-gray-700 truncate max-w-[150px]">{row[key] || '\u2014'}</td>
                              ))}
                              {hasTagColumns && (
                                <td className="px-3 py-2">{tagCount > 0 ? <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">{tagCount} tags</span> : '\u2014'}</td>
                              )}
                              {hasPosColumns && (
                                <td className="px-3 py-2">
                                  {parsedPositions.length > 0 ? (
                                    <div className="space-y-1">
                                      {parsedPositions.slice(0, 3).map((pos, pi) => (
                                        <div key={pi} className="text-xs leading-tight">
                                          <span className="font-medium text-purple-800">{pos.job_title || '?'}</span>
                                          {pos.company_name && <span className="text-gray-500"> @ {pos.company_name}</span>}
                                          {pos.start_date && <span className="text-gray-400 ml-1">({pos.start_date.substring(0,7)}{pos.end_date ? `–${pos.end_date.substring(0,7)}` : pos.is_current ? '–now' : ''})</span>}
                                        </div>
                                      ))}
                                      {parsedPositions.length > 3 && <span className="text-xs text-gray-400">+{parsedPositions.length - 3} more</span>}
                                    </div>
                                  ) : '\u2014'}
                                </td>
                              )}
                              {hasEduColumns && (
                                <td className="px-3 py-2">
                                  {parsedEducation.length > 0 ? (
                                    <div className="space-y-1">
                                      {parsedEducation.slice(0, 3).map((edu, ei) => (
                                        <div key={ei} className="text-xs leading-tight">
                                          <span className="font-medium text-emerald-800">{edu.degree_type || edu.institution || '?'}</span>
                                          {edu.field_of_study && <span className="text-gray-500">, {edu.field_of_study}</span>}
                                          {edu.institution && edu.degree_type && <span className="text-gray-500"> @ {edu.institution}</span>}
                                        </div>
                                      ))}
                                      {parsedEducation.length > 3 && <span className="text-xs text-gray-400">+{parsedEducation.length - 3} more</span>}
                                    </div>
                                  ) : '\u2014'}
                                </td>
                              )}
                              {hasCertColumns && (
                                <td className="px-3 py-2">
                                  {parsedCerts.length > 0 ? (
                                    <div className="space-y-1">
                                      {parsedCerts.slice(0, 2).map((cert, ci) => (
                                        <div key={ci} className="text-xs leading-tight">
                                          <span className="font-medium text-orange-800">{cert.certification_name}</span>
                                          {cert.issuing_organization && <span className="text-gray-500"> — {cert.issuing_organization}</span>}
                                        </div>
                                      ))}
                                      {parsedCerts.length > 2 && <span className="text-xs text-gray-400">+{parsedCerts.length - 2} more</span>}
                                    </div>
                                  ) : '\u2014'}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {parsedData?.length > 5 && <p className="text-xs text-gray-400 mt-2 text-center">Showing first 5 of {parsedData.length} rows</p>}

                  <div className="flex gap-3 pt-4">
                    <button onClick={handleImport} disabled={!!importResults?.success}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 font-medium">
                      Import {parsedData?.length} Candidates
                    </button>
                    <button onClick={onClose} className="px-4 py-2 border text-gray-700 rounded-lg hover:bg-gray-50">{importResults?.success ? 'Done' : 'Cancel'}</button>
                  </div>
                </div>
              )}

              {/* STEP: IMPORTING */}
              {uploadStep === 'importing' && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="font-medium text-gray-900">Importing candidates...</p>
                  <p className="text-sm text-gray-500 mt-1">Processing {importProgress.total} candidates</p>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-4">
                    <div className="bg-blue-600 h-2 rounded-full transition-all animate-pulse" style={{ width: '60%' }} />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddCandidateArchiveModal;
